import { useCallback, useMemo, useState } from 'react';
import { parseChantVars, type ChantVars } from '@vedaunion/shared';

/**
 * Document variables arriving in the URL, as VIEW-ONLY overrides.
 *
 * Read once, on mount, from `window.location.search` — a link is a snapshot of
 * what the sender saw, not a live channel, so a later `replaceState` by the
 * reader (which rewrites `s`/`s2`/`m`/`v` as you change script or verse) must
 * not re-apply variables the recipient has since overridden.
 *
 * `release(name)` drops one variable. Call it the moment the recipient changes
 * that control themselves: from then on their own value applies, and only then
 * is anything persisted. See `shared/src/chant-vars.ts` for why the two rules —
 * URL wins for the view, URL never writes to storage — are the whole feature.
 *
 * Nothing here touches preferences, and nothing here is stored. `name` and
 * `gotra` are personal data: they live in the URL the sender chose to make and
 * in this component's memory, and nowhere else.
 */
export function useChantVars(): { vars: ChantVars; release: (name: string) => void } {
  const initial = useMemo(
    () => (typeof window === 'undefined' ? {} : parseChantVars(window.location.search)),
    [],
  );
  const [released, setReleased] = useState<string[]>([]);
  const release = useCallback((name: string) => {
    setReleased((prev) => (prev.includes(name) ? prev : [...prev, name]));
  }, []);
  const vars = useMemo(() => {
    if (!released.length) return initial;
    const out: ChantVars = {};
    for (const [k, v] of Object.entries(initial)) if (!released.includes(k)) out[k] = v;
    return out;
  }, [initial, released]);
  return { vars, release };
}
